import apiClient from './apiService';
import { AxiosResponse } from 'axios';

const apiService = apiClient;

export interface Position {
    id: number;
    authority_id: number;
    location_id: number;
    authority_name?: string;
    location_name?: string;
    user_name?: string;
    entity_id: number;
    name: string;
    code: string;
    description?: string;
    is_active: number;  // 1=active, 0=inactive, -1=deleted
    is_approved: number;
    created_by: number;
    created_on: string;
    modified_by?: number;
    modified_on?: string;
    workflow_state?: string;
    created_by_name?: string;
    submitted_by?: number;
    submitted_on?: string;
    submitted_by_name?: string;
    approved_by?: number;
    approved_on?: string;
    approved_by_name?: string;
    approval_note?: string;
    rejected_by?: number;
    rejected_on?: string;
    rejected_by_name?: string;
    rejection_reason?: string;
}

export interface PositionCreate {
    name: string;
    code: string;
    description?: string;
    is_active: number;
    authority_id?: number;
    location_id?: number;
}

export interface PositionUpdate {
    name?: string;
    code?: string;
    description?: string;
    is_active?: number;
    authority_id?: number;
    location_id?: number;
}

export interface PositionStats {
    total: number;
    active: number;
    inactive: number;
}

interface PositionsResponse {
    data: Position[];
    total: number;
    page: number;
    limit: number;
    pages: number;
}

class OrganizationPositionsService {

    async getPositions(page: number = 1, limit: number = 10): Promise<PositionsResponse> {
        try {
            const response: AxiosResponse<any> = await apiService.get(`/api/v1/org/positions?page=${page}&limit=${limit}`);
            const body = response.data;
            // Backend returns { data: [], meta: { page, limit, total } } — flatten for component
            const meta = body.meta || {};
            const total = meta.total ?? body.total ?? 0;
            const pages = meta.pages ?? (Math.ceil(total / limit) || 1);
            return {
                data: body.data || [],
                total,
                page: meta.page ?? page,
                limit: meta.limit ?? limit,
                pages,
            };
        } catch (error: any) {
            console.error('Error fetching positions:', error);
            throw new Error(error.response?.data?.error || 'Failed to fetch positions');
        }
    }

    async getStats(): Promise<PositionStats> {
        try {
            const response: AxiosResponse<any> = await apiService.get('/api/v1/org/positions/stats');
            // Backend returns: {"total": ..., "active": ..., "inactive": ...}
            return response.data;
        } catch (error: any) {
            console.error('Error fetching stats:', error);
            throw new Error(error.response?.data?.error || 'Failed to fetch stats');
        }
    }

    async getPosition(id: number): Promise<Position> {
        try {
            const response: AxiosResponse<Position> = await apiService.get(`/api/v1/org/positions/${id}`);
            return response.data;
        } catch (error: any) {
            console.error(`Error fetching position ${id}:`, error);
            throw new Error(error.response?.data?.error || 'Failed to fetch position');
        }
    }

    async createPosition(data: PositionCreate): Promise<Position> {
        try {
            // Validate required fields before calling backend.
            if (!data.authority_id || !data.location_id) {
                throw new Error('Authority and Location are required to create a position.');
            }
            const payload = { ...data };
            const response: AxiosResponse<Position> = await apiService.post('/api/v1/org/positions', payload);
            return response.data;
        } catch (error: any) {
            console.error('Error creating position:', error);
            throw new Error(error.response?.data?.error || 'Failed to create position');
        }
    }

    async updatePosition(id: number, data: PositionUpdate): Promise<Position> {
        try {
            const response: AxiosResponse<Position> = await apiService.put(`/api/v1/org/positions/${id}`, data);
            return response.data;
        } catch (error: any) {
            console.error(`Error updating position ${id}:`, error);
            throw new Error(error.response?.data?.error || 'Failed to update position');
        }
    }

    async deletePosition(id: number): Promise<void> {
        try {
            await apiService.delete(`/api/v1/org/positions/${id}`);
        } catch (error: any) {
            console.error(`Error deleting position ${id}:`, error);
            throw new Error(error.response?.data?.error || 'Failed to delete position');
        }
    }

    async submit(id: number): Promise<void> {
        try {
            await apiService.post(`/api/v1/org/positions/${id}/submit`, {});
        } catch (error: any) {
            throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to submit position');
        }
    }

    async approve(id: number, note?: string): Promise<void> {
        try {
            await apiService.post(`/api/v1/org/positions/${id}/approve`, { note });
        } catch (error: any) {
            throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to approve position');
        }
    }

    async reject(id: number, reason: string): Promise<void> {
        try {
            await apiService.post(`/api/v1/org/positions/${id}/reject`, { reason });
        } catch (error: any) {
            throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to reject position');
        }
    }

    async reopen(id: number): Promise<void> {
        try {
            await apiService.post(`/api/v1/org/positions/${id}/reopen`, {});
        } catch (error: any) {
            throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to reopen position');
        }
    }

    async getHistory(id: number): Promise<any[]> {
        const response = await apiService.get<{ items: any[] }>(`/api/v1/org/positions/${id}/history`);
        const rows = response.data.items ?? (response.data as any) ?? [];
        return rows.map((r: any) => ({
            audit_id: r.id || `pos-${id}-${Math.random()}`,
            action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
            changed_by_username: r.changed_by_username || 'Unknown',
            changed_on: r.changed_on,
            change_reason: r.remarks,
        }));
    }
}

const organizationPositionsService = new OrganizationPositionsService();
export default organizationPositionsService;
